import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Image,
  Alert,
  ScrollView,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { usePersistentCart, PersistentCartItem } from '../context/PersistentCartContext';
import { useAuth } from '../context/AuthContext';
import { PaymentMethod, CheckoutData, ShippingAddress, Order, OrderItem } from '../types';
import APIService from '../services/api';
import WhatsAppService from '../services/whatsapp';
import DepartmentSelector, { Departamento, Municipio } from '../components/DepartmentSelector';
import OrderSummaryModal from '../components/OrderSummaryModal';

interface ShippingData {
  name: string;
  address: string;
  city: string;
  phone: string;
  department: string; // ID del departamento
  department_nombre?: string; // Nombre del departamento
  municipio?: string; // ID del municipio
  municipio_nombre?: string; // Nombre del municipio
  codigo_postal?: string; // Código postal
  references?: string;
}

const CartCheckoutScreen: React.FC = () => {
  const { 
    state,
    addToCart, 
    updateQuantity, 
    removeFromCart, 
    clearCart 
  } = usePersistentCart();
  
  const { user } = useAuth(); // Agregar contexto de autenticación
  const navigation = useNavigation(); // Agregar hook de navegación

  // Helper functions para compatibilidad
  const cart = {
    items: state.items,
    totalItems: state.itemsCount,
    itemsCount: state.itemsCount,
    subtotal: typeof state.subtotal === 'number' ? state.subtotal : parseFloat(state.subtotal) || 0
  };
  
  const getCartTotal = () => {
    return typeof state.subtotal === 'number' ? state.subtotal : parseFloat(state.subtotal) || 0;
  };

  const [currentStep, setCurrentStep] = useState<'cart' | 'checkout' | 'payment'>('cart');
  const [selectedDepartamento, setSelectedDepartamento] = useState<Departamento | undefined>(undefined);
  const [selectedMunicipio, setSelectedMunicipio] = useState<Municipio | undefined>(undefined);
  const [shippingData, setShippingData] = useState<ShippingData>({
    name: '',
    address: '',
    city: '',
    phone: '',
    department: '',
    department_nombre: '',
    municipio: '',
    municipio_nombre: '',
    codigo_postal: '',
    references: '',
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [selectedComplementaryPaymentMethod, setSelectedComplementaryPaymentMethod] = useState<PaymentMethod | null>(null);
  const [forceUpdate, setForceUpdate] = React.useState(0);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<Order | null>(null);
  const [walletBalance, setWalletBalance] = useState<string>('0.00');
  const [loadingWallet, setLoadingWallet] = useState<boolean>(true);
  const [useWalletBalance, setUseWalletBalance] = useState<boolean>(false);

  // Cargar saldo de billetera virtual
  useEffect(() => {
    const loadWalletBalance = async () => {
      if (user) {
        try {
          setLoadingWallet(true);
          console.log('💰 CartCheckoutScreen: Cargando saldo de billetera virtual');
          const walletData = await APIService.getWalletBalance();
          setWalletBalance(walletData.saldo_billetera);
          console.log('💰 CartCheckoutScreen: Saldo cargado:', walletData.saldo_billetera);
        } catch (error) {
          console.error('❌ CartCheckoutScreen: Error al cargar billetera:', error);
          setWalletBalance('0.00');
        } finally {
          setLoadingWallet(false);
        }
      }
    };

    loadWalletBalance();
  }, [user]);

  // Autocompletar datos del usuario logueado
  useEffect(() => {
    if (user) {
      console.log('👤 Autocompletando datos del usuario logueado:', user);
      
      // Autocompletar datos básicos
      setShippingData(prevData => ({
        ...prevData,
        name: user.name || prevData.name,
        phone: user.phone || user.telefono || prevData.phone,
        address: prevData.address || user.direccion || '',
        city: '', // Campo city ya no se usa
        department: prevData.department || user.departamento || '',
        department_nombre: prevData.department_nombre || '',
        municipio: prevData.municipio || user.municipio || '',
        municipio_nombre: prevData.municipio_nombre || '',
        codigo_postal: prevData.codigo_postal || '',
        references: prevData.references || '',
      }));
      
      // Si el usuario tiene departamento, cargar los datos para los selectores
      if (user.departamento && !selectedDepartamento) {
        // Cargar departamento del usuario
        APIService.getDepartamentos().then(departamentos => {
          const userDept = departamentos.find(d => d.id.toString() === user.departamento?.toString());
          if (userDept) {
            setSelectedDepartamento(userDept);
            console.log('✅ Departamento del usuario cargado:', userDept);
            
            // Si tiene municipio, cargarlo también
            if (user.municipio) {
              APIService.getMunicipios(userDept.id).then(municipios => {
                const userMun = municipios.find(m => m.id.toString() === user.municipio?.toString());
                if (userMun) {
                  setSelectedMunicipio(userMun);
                  console.log('✅ Municipio del usuario cargado:', userMun);
                }
              }).catch(err => console.error('Error cargando municipios:', err));
            }
          }
        }).catch(err => console.error('Error cargando departamentos:', err));
      }
      
      console.log('✅ Datos autocompletados:', {
        nombre: user.name,
        telefono: user.phone || user.telefono,
        direccion: user.direccion,
        departamento: user.departamento,
        municipio: user.municipio
      });
    }
  }, [user]);

  // Mock payment methods - en producción vendrían del API
  const paymentMethods: PaymentMethod[] = [
    { id_formapago: 1, idempresa: 1, descripcion: 'Efectivo', estado: 1 },
    { id_formapago: 2, idempresa: 1, descripcion: 'Tarjeta de Débito', estado: 1 },
    { id_formapago: 3, idempresa: 1, descripcion: 'Tarjeta de Crédito', estado: 1 },
    { id_formapago: 4, idempresa: 1, descripcion: 'Transferencia Bancaria', estado: 1 },
    { id_formapago: 7, idempresa: 1, descripcion: '💰 Billetera Virtual', estado: 1 },
  ];

  React.useEffect(() => {
    setForceUpdate(f => f + 1);
  }, [cart.items.length]);

  const handleQuantityChange = (productId: number, change: number) => {
    const currentItem = cart.items.find(item => item.id_producto === productId);
    if (currentItem) {
      const currentQuantity = Number(currentItem.cantidad) || 1;
      const newQuantity = currentQuantity + change;
      if (newQuantity > 0) {
        updateQuantity(productId, newQuantity);
      } else {
        removeFromCart(productId);
      }
    }
  };

  const handleRemoveItem = async (productId: number, productName: string) => {
    console.log('🗑️ Botón eliminar producto presionado:', productName);
    
    Alert.alert(
      'Eliminar producto',
      `¿Estás seguro de que quieres remover "${productName}" del carrito?`,
      [
        {
          text: 'Cancelar',
          onPress: () => console.log('❌ Usuario canceló remover producto'),
          style: 'cancel',
        },
        {
          text: 'Eliminar',
          onPress: async () => {
            console.log('✅ Usuario confirmó remover producto');
            try {
              await removeFromCart(productId);
              console.log('✅ Producto removido exitosamente');
            } catch (error) {
              console.error('❌ Error removing item:', error);
              Alert.alert('Error', 'No se pudo eliminar el producto');
            }
          },
          style: 'destructive',
        },
      ],
      { cancelable: true }
    );
  };

  const handleClearCart = async () => {
    console.log('🚨🚨🚨 BOTÓN VACIAR CARRITO PRESIONADO - CartCheckoutScreen 🚨🚨🚨');
    console.log('📊 Current cart items count:', state.items.length);
    console.log('🔧 clearCart function available:', typeof clearCart);
    console.log('🔧 state.isLoading:', state.isLoading);
    
    if (state.items.length === 0) {
      Alert.alert('Carrito vacío', 'No hay productos en el carrito para limpiar');
      return;
    }

    if (state.isLoading) {
      console.log('⚠️ Carrito está cargando, esperando...');
      return;
    }
    
    console.log('🔔 Showing confirmation dialog...');
    
    Alert.alert(
      'Vaciar carrito',
      `¿Estás seguro de que quieres eliminar todos los productos del carrito? (${state.items.length} productos)`,
      [
        {
          text: 'Cancelar',
          onPress: () => console.log('❌ Usuario canceló el vaciado del carrito'),
          style: 'cancel',
        },
        {
          text: 'Vaciar',
          onPress: async () => {
            console.log('✅ Usuario confirmó vaciar carrito');
            try {
              console.log('🗑️ Ejecutando clearCart()...');
              await clearCart();
              setCurrentStep('cart');
              console.log('✅ clearCart() completado exitosamente');
              Alert.alert('Éxito', 'El carrito ha sido vaciado correctamente');
            } catch (error) {
              console.error('❌ Error clearing cart:', error);
              Alert.alert('Error', 'No se pudo vaciar el carrito. Intenta nuevamente.');
            }
          },
          style: 'destructive',
        },
      ],
      { cancelable: true }
    );
  };

  const proceedToCheckout = () => {
    if (cart.items.length === 0) {
      Alert.alert('Carrito vacío', 'Agrega productos al carrito para continuar');
      return;
    }
    console.log('🛒 Proceeding to checkout step');
    setCurrentStep('checkout');
  };

  const proceedToPayment = () => {
    console.log('💳 Proceed to payment button pressed');
    console.log('📋 Current shipping data:', shippingData);
    console.log('📊 Required fields check:', {
      name: !!shippingData.name,
      address: !!shippingData.address,
      department: !!shippingData.department,
      municipio: !!shippingData.municipio,
      phone: !!shippingData.phone
    });
    
    if (!shippingData.name || !shippingData.address || !shippingData.department || !shippingData.municipio || !shippingData.phone) {
      console.log('❌ Missing required fields');
      Alert.alert('Datos incompletos', 'Por favor completa todos los campos de envío (nombre, dirección, departamento, municipio y teléfono)');
      return;
    }
    console.log('✅ All shipping data valid, proceeding to payment step');
    setCurrentStep('payment');
  };

  const processOrder = async () => {
    // Prevenir múltiples clicks
    if (isProcessingOrder || orderCreated) {
      console.log('🚫 Pedido ya está siendo procesado o ya fue creado');
      return;
    }

    if (!selectedPaymentMethod) {
      alert('Por favor selecciona un método de pago');
      return;
    }

    // Validar método complementario si la billetera es insuficiente
    const cartTotal = getCartTotal();
    const walletBalanceNum = parseFloat(walletBalance);
    const remainingAmount = selectedPaymentMethod?.id_formapago === 7 ? Math.max(0, cartTotal - walletBalanceNum) : 0;
    
    if (remainingAmount > 0 && !selectedComplementaryPaymentMethod) {
      alert('Tu billetera no cubre el total. Por favor selecciona un método de pago complementario para Q' + remainingAmount.toFixed(2));
      return;
    }

    console.log('🛒 Processing order...');
    console.log('📦 Shipping data:', shippingData);
    console.log('💳 Payment method:', selectedPaymentMethod);
    if (selectedComplementaryPaymentMethod) {
      console.log('💳 Complementary payment method:', selectedComplementaryPaymentMethod);
      console.log('💰 Wallet payment:', walletBalanceNum);
      console.log('💰 Complementary payment:', remainingAmount);
    }
    console.log('🛍️ Cart items:', cart.items);
    console.log('💰 Total:', cartTotal);

    // Marcar como procesando
    setIsProcessingOrder(true);

    try {
      // Calcular pagos con billetera si aplica
      const walletPayment = selectedPaymentMethod?.id_formapago === 7 ? Math.min(walletBalanceNum, cartTotal) : 0;
      const complementaryPayment = remainingAmount > 0 ? remainingAmount : 0;
      
      // Preparar datos del checkout
      const checkoutData: CheckoutData = {
        items: cart.items,
        total: getCartTotal(),
        subtotal: getCartTotal(), // Sin impuestos por ahora
        taxes: 0,
        shipping: 0,
        paymentMethod: selectedPaymentMethod,
        complementaryPaymentMethod: selectedComplementaryPaymentMethod,
        walletPayment: walletPayment,
        complementaryPayment: complementaryPayment,
        shippingAddress: {
          name: shippingData.name,
          address: shippingData.address,
          city: shippingData.city,
          phone: shippingData.phone,
          department: shippingData.department,
          municipio: shippingData.municipio, // Agregar municipio
          references: shippingData.references
        },
        notes: 'Pedido desde MotoMarketGT App'
      };

      console.log('🔄 Enviando pedido al servidor...', checkoutData);
      console.log('📍 SHIPPING DATA FINAL:', {
        department: shippingData.department,
        municipio: shippingData.municipio,
        departmentNombre: shippingData.department_nombre,
        municipioNombre: shippingData.municipio_nombre
      });
      console.log('🌐 Backend URL: http://127.0.0.1:8000/api/mobile/pedido');

      // Llamar al API
      const order: Order = await APIService.processCheckout(checkoutData);

      console.log('✅ Pedido procesado exitosamente:', order);
      console.log('📋 Número de pedido:', order.orderNumber);
      console.log('💰 Total final:', order.total);

      // Marcar pedido como creado
      setOrderCreated(true);

      // ✅ SIEMPRE RECARGAR SALDO DE BILLETERA después de cada pedido exitoso
      console.log('💰 Recargando saldo de billetera después del pedido...');
      let newBalance = walletBalance; // Usar saldo actual como fallback
      try {
        const updatedWalletData = await APIService.getWalletBalance();
        newBalance = updatedWalletData.saldo_billetera;
        setWalletBalance(newBalance);
        console.log('✅ Saldo de billetera actualizado a:', newBalance);
      } catch (error) {
        console.error('❌ Error recargando saldo de billetera:', error);
        // Calcular saldo manualmente si falla la recarga
        newBalance = (parseFloat(walletBalance) - walletPayment).toFixed(2);
      }

      // Crear versión simplificada del pedido para el modal
      const simpleOrderForModal = {
        id: order.id,
        orderNumber: order.orderNumber,
        userId: user?.id || 0,
        items: state.items.map((item: PersistentCartItem) => ({
          product: {
            id: item.id_producto,
            idempresa: 0,
            nombre: item.producto_nombre,
            imagen: item.imagen || '',
            descripcion: '',
            monto_billetera: '0',
            stock: item.stock || 0,
            linea_id: 0,
            activo: true,
            visible_registrados: true,
            nivel_cliente: 1,
            precio: item.precio_unitario.toString(),
            created_at: '',
            categoria_id: 0,
            estado: 1,
            updated_at: ''
          },
          quantity: item.cantidad,
          subtotal: item.subtotal,
          total: item.subtotal
        })),
        total: order.total,
        subtotal: order.total,
        taxes: 0,
        shipping: 0,
        walletPayment: walletPayment,
        complementaryPayment: complementaryPayment,
        newWalletBalance: newBalance,
        status: 'confirmado' as const,
        paymentStatus: 'pagado' as const,
        paymentMethod: selectedPaymentMethod,
        shippingAddress: {
          name: shippingData.name,
          address: shippingData.address,
          city: shippingData.city,
          phone: shippingData.phone,
          department: shippingData.department_nombre,
          references: shippingData.references
        },
        createdAt: new Date().toISOString()
      };

      // Limpiar carrito inmediatamente después de crear el pedido exitosamente
      console.log('🧹 Limpiando carrito después de crear pedido exitosamente');
      try {
        await clearCart();
        console.log('✅ Carrito limpiado exitosamente después de crear pedido');
      } catch (clearError) {
        console.error('❌ Error limpiando carrito:', clearError);
      }

      // Mostrar modal de resumen detallado
      setCompletedOrder(simpleOrderForModal as Order);
      setShowOrderSummary(true);

    } catch (error: any) {
      console.error('❌ Error procesando pedido:', error);
      console.error('📄 Error details:', error.message);
      console.error('🔍 Response data:', error.response?.data);
      
      // Si hay errores de validación específicos
      if (error.response?.data?.errors) {
        console.error('❌ Errores de validación:', error.response.data.errors);
        Object.keys(error.response.data.errors).forEach(field => {
          console.error(`  - ${field}: ${error.response.data.errors[field].join(', ')}`);
        });
      }
      
      Alert.alert(
        'Error en el pedido',
        `No se pudo procesar tu pedido: ${error.response?.data?.message || error.message}\n\nPor favor intenta nuevamente.`,
        [{ text: 'OK' }]
      );
    } finally {
      // Restablecer estado de procesamiento en caso de error
      setIsProcessingOrder(false);
    }
  };

  // Función para manejar el cierre del modal de resumen de pedido
  const handleCloseOrderSummary = async () => {
    setShowOrderSummary(false);
    
    // Enviar mensaje de confirmación a WhatsApp usando el servicio centralizado
    if (completedOrder) {
      try {
        console.log('📱 Obteniendo configuración para WhatsApp');
        const config = await APIService.getEmpresaConfig();
        const whatsappNumber = config.whatsapp;
        
        if (whatsappNumber) {
          // Preparar datos del pedido para el servicio de WhatsApp
          const orderData = {
            orderNumber: completedOrder.orderNumber,
            total: completedOrder.total,
            walletPayment: (completedOrder as any).walletPayment || 0,
            newWalletBalance: (completedOrder as any).newWalletBalance || 0,
            items: completedOrder.items?.map(item => {
              const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
              const quantity = item.quantity || 1;
              const unitPrice = itemTotal / quantity;
              
              return {
                name: item.product?.nombre || 'Producto',
                quantity: quantity,
                price: unitPrice
              };
            }) || []
          };
          
          console.log('📱 Enviando mensaje de confirmación de pedido');
          await WhatsAppService.sendOrderConfirmation(orderData, whatsappNumber);
          console.log('✅ Mensaje de WhatsApp enviado exitosamente');
        }
      } catch (error) {
        console.error('❌ Error enviando mensaje de WhatsApp:', error);
        // No mostrar error al usuario, solo continuar con el flujo normal
      }
    }
    
    setCompletedOrder(null);
    
    // El carrito ya fue limpiado al crear el pedido
    console.log('🏠 Cerrando resumen de pedido');
    
    // Resetear estado del checkout
    setCurrentStep('cart');
    setShippingData({ 
      name: '', 
      address: '', 
      city: '', 
      phone: '', 
      department: '', 
      department_nombre: '', 
      municipio: '', 
      municipio_nombre: '', 
      codigo_postal: '', 
      references: '' 
    });
    setSelectedPaymentMethod(null);
    setOrderCreated(false);
    setIsProcessingOrder(false);
    
    // Navegar al dashboard (MainTabs -> Dashboard)
    console.log('🏠 Navegando al dashboard después de cerrar resumen');
    (navigation as any).navigate('MainTabs', { 
      screen: 'Dashboard'
    });
  };

  const renderCartItem = ({ item }: { item: PersistentCartItem }) => {
    const itemTotal = parseFloat(String(item.precio_unitario || 0)) * item.cantidad;

    return (
      <View style={styles.cartItem}>
        <View style={styles.productImageContainer}>
          {item.imagen ? (
            <Image 
              source={{ uri: item.imagen }}
              style={styles.productImage}
              onError={() => console.log('Error loading product image:', item.imagen)}
            />
          ) : (
            <View style={[styles.productImage, styles.placeholderImage]}>
              <Text style={styles.placeholderText}>📦</Text>
            </View>
          )}
        </View>
        
        <View style={styles.productInfo}>
          <Text style={styles.productName} numberOfLines={2}>
            {item.producto_nombre}
          </Text>
          <Text style={styles.productPrice}>
            Q{parseFloat(String(item.precio_unitario || 0)).toFixed(2)} c/u
          </Text>
        </View>

        <View style={styles.quantityControls}>
          <TouchableOpacity
            style={styles.quantityButton}
            onPress={() => handleQuantityChange(item.id_producto, -1)}
          >
            <Text style={styles.quantityButtonText}>-</Text>
          </TouchableOpacity>
          
          <Text style={styles.quantity}>{item.cantidad}</Text>
          
          <TouchableOpacity
            style={styles.quantityButton}
            onPress={() => handleQuantityChange(item.id_producto, 1)}
          >
            <Text style={styles.quantityButtonText}>+</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.itemActions}>
          <Text style={styles.itemTotal}>
            Q{itemTotal.toFixed(2)}
          </Text>
          {currentStep === 'cart' && (
            <TouchableOpacity
              style={styles.removeButton}
              onPress={() => handleRemoveItem(item.id_producto, item.producto_nombre)}
            >
              <Text style={styles.removeButtonText}>🗑️</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderEmptyCart = () => (
    <View style={styles.emptyCart}>
      <Text style={styles.emptyCartIcon}>🛒</Text>
      <Text style={styles.emptyCartTitle}>Tu carrito está vacío</Text>
      <Text style={styles.emptyCartSubtitle}>
        Agrega productos para comenzar tu compra
      </Text>
    </View>
  );

  const renderStepIndicator = () => (
    <View style={styles.stepIndicator}>
      <View style={[styles.step, currentStep === 'cart' && styles.activeStep]}>
        <Text style={[styles.stepText, currentStep === 'cart' && styles.activeStepText]}>1. Carrito</Text>
      </View>
      <View style={[styles.step, currentStep === 'checkout' && styles.activeStep]}>
        <Text style={[styles.stepText, currentStep === 'checkout' && styles.activeStepText]}>2. Envío</Text>
      </View>
      <View style={[styles.step, currentStep === 'payment' && styles.activeStep]}>
        <Text style={[styles.stepText, currentStep === 'payment' && styles.activeStepText]}>3. Pago</Text>
      </View>
    </View>
  );

  const renderShippingForm = () => (
    <View style={styles.shippingForm}>
      <Text style={styles.sectionTitle}>Información de Envío</Text>
      
      <TextInput
        style={styles.input}
        placeholder="Nombre completo *"
        value={shippingData.name}
        onChangeText={(text) => setShippingData({...shippingData, name: text})}
      />
      
      <TextInput
        style={[styles.input, { minHeight: 60 }]}
        placeholder="Dirección completa *"
        value={shippingData.address}
        onChangeText={(text) => setShippingData({...shippingData, address: text})}
        multiline
        numberOfLines={3}
        textAlignVertical="top"
        editable={true}
      />
      
      {/* DEPARTAMENTO Y MUNICIPIO - Datos obtenidos de la tabla clientes */}
      <View style={styles.departmentContainer}>
        <Text style={styles.departmentLabel}>Departamento y Municipio *</Text>
        <DepartmentSelector
          selectedDepartamento={selectedDepartamento}
          selectedMunicipio={selectedMunicipio}
          onDepartamentoChange={(departamento) => {
            console.log('🏛️ CartCheckout: Departamento seleccionado:', departamento);
            setSelectedDepartamento(departamento);
            setSelectedMunicipio(undefined); // Reset municipio
            setShippingData({...shippingData, department: departamento.id.toString(), department_nombre: departamento.nombre, municipio: '', municipio_nombre: '', codigo_postal: ''});
          }}
          onMunicipioChange={(municipio) => {
            console.log('🏛️ CartCheckout: Municipio seleccionado:', municipio);
            setSelectedMunicipio(municipio);
            setShippingData({...shippingData, municipio: municipio.id.toString(), municipio_nombre: municipio.nombre, codigo_postal: municipio.codigo_postal});
          }}
          style={styles.departmentSelector}
        />
      </View>
      
      <TextInput
        style={styles.input}
        placeholder="Teléfono *"
        value={shippingData.phone}
        onChangeText={(text) => setShippingData({...shippingData, phone: text})}
        keyboardType="phone-pad"
      />
      
      <TextInput
        style={styles.input}
        placeholder="Referencias de ubicación (opcional)"
        value={shippingData.references}
        onChangeText={(text) => setShippingData({...shippingData, references: text})}
        multiline
        numberOfLines={2}
      />
    </View>
  );

  const renderPaymentMethods = () => {
    const cartTotal = getCartTotal();
    const walletBalanceNum = parseFloat(walletBalance);
    const walletPayment = selectedPaymentMethod?.id_formapago === 7 ? Math.min(walletBalanceNum, cartTotal) : 0;
    const remainingAmount = selectedPaymentMethod?.id_formapago === 7 ? Math.max(0, cartTotal - walletBalanceNum) : cartTotal;
    
    return (
      <View style={styles.paymentSection}>
        <Text style={styles.sectionTitle}>Método de Pago</Text>
        
        {paymentMethods.map((method) => {
          // Para Billetera Virtual, mostrar si hay saldo insuficiente
          const isWallet = method.id_formapago === 7;
          const isDisabled = isWallet && walletBalanceNum <= 0;
          
          return (
            <TouchableOpacity
              key={method.id_formapago}
              style={[
                styles.paymentMethod,
                selectedPaymentMethod?.id_formapago === method.id_formapago && styles.selectedPaymentMethod,
                isDisabled && styles.paymentMethodDisabled
              ]}
              onPress={() => {
                if (isDisabled) {
                  alert('No tienes saldo disponible en tu billetera virtual');
                  return;
                }
                setSelectedPaymentMethod(method);
              }}
            >
              <View style={styles.radioButton}>
                {selectedPaymentMethod?.id_formapago === method.id_formapago && (
                  <View style={styles.radioButtonSelected} />
                )}
              </View>
              <View style={styles.paymentMethodContent}>
                <Text style={[
                  styles.paymentMethodText,
                  isDisabled && styles.paymentMethodTextDisabled
                ]}>
                  {method.descripcion}
                </Text>
                {isWallet && (
                  <Text style={[
                    styles.paymentMethodBalance,
                    isDisabled && styles.paymentMethodTextDisabled
                  ]}>
                    Disponible: Q{walletBalance}
                  </Text>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
        
        {/* Mostrar desglose cuando se selecciona Billetera Virtual */}
        {selectedPaymentMethod?.id_formapago === 7 && (
          <View style={styles.walletBreakdown}>
            <Text style={styles.walletBreakdownTitle}>💰 Desglose de Pago con Billetera</Text>
            
            <View style={styles.breakdownRow}>
              <Text style={styles.breakdownLabel}>Total del pedido:</Text>
              <Text style={styles.breakdownValue}>Q{cartTotal.toFixed(2)}</Text>
            </View>
            
            <View style={styles.breakdownRow}>
              <Text style={styles.breakdownLabel}>Se pagará con billetera:</Text>
              <Text style={styles.breakdownValueHighlight}>-Q{walletPayment.toFixed(2)}</Text>
            </View>
            
            {remainingAmount > 0 ? (
              <>
                <View style={[styles.breakdownRow, styles.breakdownRowWarning]}>
                  <Text style={styles.breakdownLabelWarning}>⚠️ Saldo insuficiente</Text>
                </View>
                <View style={styles.breakdownRow}>
                  <Text style={styles.breakdownLabel}>Monto restante:</Text>
                  <Text style={styles.breakdownValueWarning}>Q{remainingAmount.toFixed(2)}</Text>
                </View>
                <View style={styles.walletWarningBox}>
                  <Text style={styles.walletWarningText}>
                    Selecciona un método de pago adicional para cubrir Q{remainingAmount.toFixed(2)}
                  </Text>
                </View>
                
                {/* Selector de método de pago complementario */}
                <View style={styles.complementaryPaymentSection}>
                  <Text style={styles.complementaryPaymentTitle}>
                    💳 Método de Pago Complementario *
                  </Text>
                  {paymentMethods
                    .filter(method => method.id_formapago !== 7) // Excluir Billetera Virtual
                    .map((method) => (
                      <TouchableOpacity
                        key={method.id_formapago}
                        style={[
                          styles.complementaryPaymentMethod,
                          selectedComplementaryPaymentMethod?.id_formapago === method.id_formapago && styles.selectedPaymentMethod
                        ]}
                        onPress={() => setSelectedComplementaryPaymentMethod(method)}
                      >
                        <View style={styles.radioButton}>
                          {selectedComplementaryPaymentMethod?.id_formapago === method.id_formapago && (
                            <View style={styles.radioButtonSelected} />
                          )}
                        </View>
                        <Text style={styles.paymentMethodText}>{method.descripcion}</Text>
                      </TouchableOpacity>
                    ))}
                </View>
              </>
            ) : (
              <View style={styles.breakdownRow}>
                <Text style={styles.breakdownLabelSuccess}>✅ Pago completo con billetera</Text>
              </View>
            )}
            
            <View style={[styles.breakdownRow, styles.breakdownRowFinal]}>
              <Text style={styles.breakdownLabelFinal}>Saldo restante en billetera:</Text>
              <Text style={styles.breakdownValueFinal}>Q{Math.max(0, walletBalanceNum - cartTotal).toFixed(2)}</Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  if (cart.items.length === 0 && currentStep === 'cart') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Mi Carrito</Text>
        </View>
        {renderEmptyCart()}
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        style={{ flex: 1 }} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            {currentStep === 'cart' ? 'Mi Carrito' : 
             currentStep === 'checkout' ? 'Información de Envío' : 'Método de Pago'}
          </Text>
          {currentStep === 'cart' && (
            <TouchableOpacity 
              onPress={() => {
                console.log('🚨🚨🚨 BOTÓN VACIAR EN CHECKOUT PRESIONADO 🚨🚨🚨');
                if (state.isLoading || state.items.length === 0) {
                  console.log('⛔ Botón deshabilitado, ignorando click');
                  return;
                }
                handleClearCart();
              }}
              style={[styles.clearButton, (state.isLoading || state.items.length === 0) && styles.clearButtonDisabled]}
            >
              <Text style={styles.clearButtonText}>
                {state.isLoading ? '⏳' : '🗑️'} Vaciar
              </Text>
            </TouchableOpacity>
          )}
          {currentStep !== 'cart' && (
            <TouchableOpacity 
              onPress={() => setCurrentStep(currentStep === 'checkout' ? 'cart' : 'checkout')} 
              style={styles.backButton}
            >
              <Text style={styles.backButtonText}>← Atrás</Text>
            </TouchableOpacity>
          )}
        </View>

        {renderStepIndicator()}

        <ScrollView 
          style={styles.content} 
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled={true}
        >
        {/* Resumen del carrito siempre visible */}
        <View style={styles.cartSummary}>
          <Text style={styles.summaryTitle}>Resumen del Pedido</Text>
          <FlatList
            data={cart.items}
            renderItem={renderCartItem}
            keyExtractor={(item) => item.id_producto?.toString() || Math.random().toString()}
            scrollEnabled={false}
            extraData={forceUpdate}
          />
        </View>

        {/* Formulario de envío en step checkout */}
        {currentStep === 'checkout' && renderShippingForm()}

        {/* Métodos de pago en step payment */}
        {currentStep === 'payment' && renderPaymentMethods()}
      </ScrollView>

      {/* Footer con total y botón de acción */}
      <View style={styles.footer}>
        <View style={styles.totalContainer}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              Productos ({cart.itemsCount})
            </Text>
            <Text style={styles.totalValue}>
              Q{cart.subtotal.toFixed(2)}
            </Text>
          </View>
          
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Envío</Text>
            <Text style={styles.totalValue}>Gratis</Text>
          </View>
          
          {/* Mostrar saldo de billetera virtual en todos los pasos */}
          <View style={styles.walletRow}>
            <Text style={styles.walletLabel}>💰 Billetera Virtual:</Text>
            {loadingWallet ? (
              <Text style={styles.walletValue}>Cargando...</Text>
            ) : (
              <Text style={styles.walletValue}>Q{walletBalance}</Text>
            )}
          </View>
          
          {/* Nota informativa en paso de carrito */}
          {currentStep === 'cart' && parseFloat(walletBalance) > 0 && (
            <View style={styles.walletInfoRow}>
              <Text style={styles.walletInfoText}>
                ℹ️ Disponible para usar como forma de pago
              </Text>
            </View>
          )}
          
          {/* Opción para usar billetera si está en paso de pago */}
          {currentStep === 'payment' && parseFloat(walletBalance) > 0 && selectedPaymentMethod?.id_formapago === 7 && (
            <View style={styles.walletInfoRow}>
              <Text style={styles.walletInfoText}>
                ℹ️ Se descontará de tu billetera virtual
              </Text>
            </View>
          )}
          
          <View style={[styles.totalRow, styles.grandTotalRow]}>
            <Text style={styles.grandTotalLabel}>Total</Text>
            <Text style={styles.grandTotalValue}>
              Q{getCartTotal().toFixed(2)}
            </Text>
          </View>
        </View>

        <TouchableOpacity 
          style={[
            styles.actionButton,
            (isProcessingOrder || orderCreated) && styles.disabledButton
          ]}
          disabled={isProcessingOrder || orderCreated}
          onPress={() => {
            console.log('🔘 Action button pressed, current step:', currentStep);
            if (currentStep === 'cart') {
              console.log('📦 Executing proceedToCheckout');
              proceedToCheckout();
            } else if (currentStep === 'checkout') {
              console.log('💳 Executing proceedToPayment');
              proceedToPayment();
            } else {
              console.log('🛍️ Executing processOrder');
              processOrder();
            }
          }}
          activeOpacity={0.7}
        >
          <Text style={[
            styles.actionButtonText,
            (isProcessingOrder || orderCreated) && styles.disabledButtonText
          ]}>
            {currentStep === 'cart' ? `Proceder al Checkout (${cart.items.length} productos)` :
             currentStep === 'checkout' ? 'Continuar al Pago' :
             isProcessingOrder ? '🔄 Procesando pedido...' :
             orderCreated ? '✅ Pedido creado' :
             'Finalizar Compra'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Modal de resumen de pedido */}
      <OrderSummaryModal
        visible={showOrderSummary}
        order={completedOrder}
        onClose={handleCloseOrderSummary}
      />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  clearButton: {
    backgroundColor: '#95a5a6',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  clearButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.5,
  },
  clearButtonText: {
    color: 'white',
    fontSize: 13,
    fontWeight: '600',
  },
  backButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  backButtonText: {
    color: '#e74c3c',
    fontSize: 16,
    fontWeight: '500',
  },
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: 'white',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  step: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
  },
  activeStep: {
    backgroundColor: '#e74c3c',
  },
  stepText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  activeStepText: {
    color: 'white',
  },
  content: {
    flex: 1,
  },
  cartSummary: {
    backgroundColor: 'white',
    margin: 16,
    borderRadius: 12,
    padding: 16,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  cartItem: {
    flexDirection: 'row',
    backgroundColor: '#f9f9f9',
    padding: 12,
    marginBottom: 8,
    borderRadius: 8,
    alignItems: 'center',
  },
  productImageContainer: {
    width: 50,
    height: 50,
  },
  productImage: {
    width: 50,
    height: 50,
    borderRadius: 6,
    backgroundColor: '#f0f0f0',
  },
  placeholderImage: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#e0e0e0',
  },
  placeholderText: {
    fontSize: 20,
  },
  productInfo: {
    flex: 1,
    marginLeft: 12,
  },
  productName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  productCategory: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  productPrice: {
    fontSize: 12,
    fontWeight: '500',
    color: '#e74c3c',
  },
  quantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 8,
  },
  quantityButton: {
    width: 24,
    height: 24,
    backgroundColor: '#e0e0e0',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quantityButtonText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
  },
  quantity: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginHorizontal: 8,
    minWidth: 16,
    textAlign: 'center',
  },
  itemActions: {
    alignItems: 'center',
  },
  itemTotal: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  removeButton: {
    padding: 2,
  },
  removeButtonText: {
    fontSize: 14,
  },
  shippingForm: {
    backgroundColor: 'white',
    margin: 16,
    borderRadius: 12,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 12,
    backgroundColor: 'white',
  },
  paymentSection: {
    backgroundColor: 'white',
    margin: 16,
    borderRadius: 12,
    padding: 16,
  },
  paymentMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    marginBottom: 8,
  },
  selectedPaymentMethod: {
    borderColor: '#e74c3c',
    backgroundColor: '#fff5f5',
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#ddd',
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#e74c3c',
  },
  paymentMethodText: {
    fontSize: 16,
    color: '#333',
  },
  paymentMethodDisabled: {
    backgroundColor: '#f5f5f5',
    opacity: 0.6,
  },
  paymentMethodTextDisabled: {
    color: '#999',
  },
  paymentMethodContent: {
    flex: 1,
  },
  paymentMethodBalance: {
    fontSize: 13,
    color: '#27ae60',
    marginTop: 2,
    fontWeight: '600',
  },
  walletBreakdown: {
    marginTop: 20,
    padding: 16,
    backgroundColor: '#f0f8f4',
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
  },
  walletBreakdownTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#27ae60',
    marginBottom: 12,
    textAlign: 'center',
  },
  breakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  breakdownLabel: {
    fontSize: 14,
    color: '#555',
  },
  breakdownValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  breakdownValueHighlight: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#27ae60',
  },
  breakdownRowWarning: {
    backgroundColor: '#fff3cd',
    paddingHorizontal: 8,
    borderRadius: 6,
    marginTop: 8,
  },
  breakdownLabelWarning: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#856404',
  },
  breakdownValueWarning: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
  walletWarningBox: {
    backgroundColor: '#fff3cd',
    padding: 10,
    borderRadius: 6,
    marginTop: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#ffc107',
  },
  walletWarningText: {
    fontSize: 13,
    color: '#856404',
    textAlign: 'center',
  },
  breakdownLabelSuccess: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#27ae60',
  },
  breakdownRowFinal: {
    backgroundColor: '#e8f5e9',
    paddingHorizontal: 8,
    borderRadius: 6,
    marginTop: 8,
    borderBottomWidth: 0,
  },
  breakdownLabelFinal: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2e7d32',
  },
  breakdownValueFinal: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#2e7d32',
  },
  complementaryPaymentSection: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  complementaryPaymentTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: 12,
  },
  complementaryPaymentMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    marginBottom: 8,
    backgroundColor: 'white',
  },
  emptyCart: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyCartIcon: {
    fontSize: 80,
    marginBottom: 20,
  },
  emptyCartTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
    textAlign: 'center',
  },
  emptyCartSubtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    lineHeight: 24,
  },
  footer: {
    backgroundColor: 'white',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
  },
  totalContainer: {
    marginBottom: 16,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  totalLabel: {
    fontSize: 16,
    color: '#666',
  },
  totalValue: {
    fontSize: 16,
    color: '#333',
    fontWeight: '500',
  },
  grandTotalRow: {
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
    paddingTop: 12,
    marginTop: 8,
  },
  grandTotalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  grandTotalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
  walletRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#ecf9f2',
    borderRadius: 8,
    marginVertical: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
  },
  walletLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#27ae60',
  },
  walletValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#27ae60',
  },
  walletInfoRow: {
    backgroundColor: '#e3f2fd',
    padding: 8,
    borderRadius: 6,
    marginBottom: 8,
  },
  walletInfoText: {
    fontSize: 13,
    color: '#1976d2',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  actionButton: {
    backgroundColor: '#e74c3c',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  actionButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  disabledButton: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  disabledButtonText: {
    color: '#7f8c8d',
  },
  // Estilos para el selector de departamentos
  departmentContainer: {
    marginBottom: 16,
  },
  departmentLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  departmentSelector: {
    // El componente DepartmentSelector manejará sus propios estilos
  },
});

export default CartCheckoutScreen;
